import { useEffect, useReducer, useRef, useState } from "react";
import { listSites } from "../lib/authClient";
import { siteKey } from "../lib/siteData";
import { streamChat, type ChatMessage, type ChatEvent, type WireMessage, type ClientToolCall } from "../lib/chatClient";
import { dispatchWpRead } from "../lib/chatWpRead";
import { dispatchWrite, buildApprovalDetail, WRITE_TOOL_NAMES, type ApprovalDetail } from "../lib/chatWriteDispatch";
import type { WPClient } from "../lib/wpClient";
import type { SeoPlugin } from "../types";

// Conversational SEO — a Claude-powered chat that reports on GSC/GA data, analyzes competitor
// opportunity gaps, and reads the connected WordPress site. The server runs the tool-use loop and
// streams text + tool status; WordPress READ tools execute here on the client (P2). WordPress
// create/edit (with approval) lands in a later phase.

interface Props { siteUrl?: string; siteBrand?: string; connected: boolean; getClient?: () => WPClient | null; plugin?: SeoPlugin }

interface AwaitingPayload { messages: unknown[]; serverResults: unknown[]; clientTools: ClientToolCall[] }
interface ToolResultBlock { type: "tool_result"; tool_use_id: string; content: string; is_error: boolean }

const HISTORY_KEY = "seo_chat_history";

interface ToolChip { id: string; name: string; ok?: boolean; error?: string }

// Tiny, safe Markdown → HTML (escape first, then a handful of inline/block rules incl. tables
// and fenced code blocks). Avoids a new dependency.
const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const splitTableRow = (line: string): string[] => line.trim().replace(/^\||\|$/g, "").split("|").map(c => c.trim());

// The model sometimes runs sentences together ("done.Next"). Insert a space after sentence-ending
// punctuation when it's immediately followed by a capital letter. Guarded so it won't touch
// decimals (3.14), lowercase domains (example.com), or ALL-CAPS abbreviations (U.S.).
function fixSpacing(s: string): string {
  return s.replace(/([a-z0-9)\]"'%])([.!?])([A-Z])/g, "$1$2 $3");
}
function renderMarkdown(md: string): string {
  const inline = (s: string) => esc(s)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  const lines = md.replace(/\r/g, "").split("\n");
  const out: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let inCode = false;
  let code: string[] = [];
  const closeList = () => { if (listType) { out.push(`</${listType}>`); listType = null; } };
  for (let idx = 0; idx < lines.length; idx++) {
    const raw = lines[idx];
    if (raw.trim().startsWith("```")) {
      if (inCode) { out.push(`<pre><code>${esc(code.join("\n"))}</code></pre>`); code = []; inCode = false; }
      else { closeList(); inCode = true; }
      continue;
    }
    if (inCode) { code.push(raw); continue; }
    const line = raw.trimEnd();
    // Table: a "| … |" row followed by a "|---|" separator.
    if (/^\s*\|.*\|\s*$/.test(line) && idx + 1 < lines.length && /-/.test(lines[idx + 1]) && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[idx + 1])) {
      closeList();
      const headers = splitTableRow(line);
      idx++; // consume separator
      const body: string[][] = [];
      while (idx + 1 < lines.length && /^\s*\|.*\|\s*$/.test(lines[idx + 1])) { idx++; body.push(splitTableRow(lines[idx])); }
      let t = "<table><thead><tr>" + headers.map(h => `<th>${inline(h)}</th>`).join("") + "</tr></thead><tbody>";
      for (const r of body) t += "<tr>" + r.map(c => `<td>${inline(c)}</td>`).join("") + "</tr>";
      out.push(t + "</tbody></table>");
      continue;
    }
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    const ul = line.match(/^\s*[-*]\s+(.*)$/);
    const ol = line.match(/^\s*\d+\.\s+(.*)$/);
    if (h) { closeList(); const lvl = Math.min(h[1].length + 2, 6); out.push(`<h${lvl}>${inline(h[2])}</h${lvl}>`); }
    else if (ul) { if (listType !== "ul") { closeList(); out.push("<ul>"); listType = "ul"; } out.push(`<li>${inline(ul[1])}</li>`); }
    else if (ol) { if (listType !== "ol") { closeList(); out.push("<ol>"); listType = "ol"; } out.push(`<li>${inline(ol[1])}</li>`); }
    else if (line === "") { closeList(); }
    else { closeList(); out.push(`<p>${inline(line)}</p>`); }
  }
  if (inCode && code.length) out.push(`<pre><code>${esc(code.join("\n"))}</code></pre>`);
  closeList();
  return out.join("\n");
}

const TOOL_LABELS: Record<string, string> = {
  site_context: "Site context", gsc_low_hanging_keywords: "GSC · low-hanging keywords", gsc_cannibalization: "GSC · cannibalization",
  gsc_serp_flux: "GSC · SERP flux", gsc_query: "GSC · query", ga_report: "GA · report", ga_ai_traffic: "GA · AI traffic",
  referring_domains: "Referring domains", keyword_gaps: "Keyword gaps", serp_results: "Live SERP", gbp_map_pack: "Map pack",
  brand_landscape: "Brand mentions", citation_comparison: "Citations", ai_visibility: "AI visibility", llm_query: "AI engine query",
  wp_generate_content: "Generating content", wp_list_pages: "Listing pages", wp_get_page: "Reading page", wp_site_info: "Site info",
  wp_create_page: "Create page", wp_update_content: "Update content", wp_update_seo_meta: "Update SEO meta", wp_set_schema: "Add schema", wp_set_h1: "Set H1",
};
const toolLabel = (name: string) => TOOL_LABELS[name] ?? name;

const PROMPT_SUGGESTIONS = [
  "What are my best low-hanging keyword opportunities?",
  "How did organic clicks trend over the last 28 days?",
  "Where are my competitors beating me — links, brand, local, content gaps?",
  "List my service pages.",
];

// ── Module-level chat session ──────────────────────────────────────────────────────────────────
// The streamed turn loop runs here, NOT inside the component, so an in-flight reply keeps going when
// the user switches to another tab (which unmounts the component). Live state (streaming text, tool
// chips, a pending write-approval) lives in `chatLive`; the component subscribes via `chatSubs` and
// re-renders. The finished reply is written straight to localStorage, so it's never lost.
interface ChatLive { streaming: boolean; liveText: string; chips: ToolChip[]; error: string; pending: { id: string; detail: ApprovalDetail } | null; }
const chatLive: ChatLive = { streaming: false, liveText: "", chips: [], error: "", pending: null };
let chatAbort: AbortController | null = null;
let chatApprovalResolver: ((v: boolean) => void) | null = null;
const chatSubs = new Set<() => void>();
const emitChat = () => chatSubs.forEach(f => f());
const patchChat = (p: Partial<ChatLive>) => { Object.assign(chatLive, p); emitChat(); };

function appendChatHistory(msg: ChatMessage) {
  try {
    const cur = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]");
    const arr = Array.isArray(cur) ? cur : [];
    localStorage.setItem(HISTORY_KEY, JSON.stringify([...arr, msg].slice(-40)));
  } catch { /* ignore */ }
}

// Human-in-the-loop gate for WordPress writes: the turn loop awaits this; the Approve/Reject buttons
// (or Stop) resolve it. Both live at module level so a tab switch mid-approval doesn't drop it.
function chatRequestApproval(id: string, detail: ApprovalDetail): Promise<boolean> {
  patchChat({ pending: { id, detail } });
  return new Promise<boolean>(resolve => { chatApprovalResolver = resolve; });
}
function chatResolveApproval(v: boolean) { const r = chatApprovalResolver; chatApprovalResolver = null; patchChat({ pending: null }); r?.(v); }

// Run one full turn. `wire0` is the visible history incl. the just-sent user message. Appends the
// assistant reply to localStorage on completion. Survives the component unmounting.
async function runChatTurn(siteId: string, wire0: ChatMessage[], getClient: (() => WPClient | null) | undefined, plugin: SeoPlugin) {
  if (chatLive.streaming) return;
  const ac = new AbortController();
  chatAbort = ac;
  let acc = "";
  patchChat({ streaming: true, liveText: "", chips: [], error: "", pending: null });
  const onEvent = (e: ChatEvent, sink: { v: AwaitingPayload | null }) => {
    if (e.type === "text" && e.delta) { acc += e.delta; patchChat({ liveText: acc }); }
    else if (e.type === "tool_call" && e.id) { const id = e.id, name = e.name ?? "tool"; if (!chatLive.chips.some(x => x.id === id)) patchChat({ chips: [...chatLive.chips, { id, name }] }); }
    else if (e.type === "tool_result" && e.id) { patchChat({ chips: chatLive.chips.map(x => x.id === e.id ? { ...x, ok: e.ok, error: e.error } : x) }); }
    else if (e.type === "awaiting_client_tools") { sink.v = { messages: e.messages ?? [], serverResults: e.serverResults ?? [], clientTools: e.clientTools ?? [] }; }
    else if (e.type === "error") { patchChat({ error: e.message ?? "Something went wrong." }); }
  };
  try {
    let wire: WireMessage[] = wire0;
    for (let hop = 0; hop < 10; hop++) {
      const sink: { v: AwaitingPayload | null } = { v: null };
      await streamChat({ siteId, messages: wire }, e => onEvent(e, sink), ac.signal);
      if (ac.signal.aborted || !sink.v) break;
      const client = getClient?.() ?? null;
      const clientResults: ToolResultBlock[] = [];
      for (const t of sink.v.clientTools) {
        if (ac.signal.aborted) break;
        let out: { ok: boolean; content: string };
        if (!client) {
          out = { ok: false, content: "WordPress isn't connected — connect it at the top of the page." };
        } else if (WRITE_TOOL_NAMES.has(t.name)) {
          const detail = await buildApprovalDetail(t.name, t.input, client, plugin);
          if (ac.signal.aborted) break;
          const approved = await chatRequestApproval(t.id, detail);
          if (ac.signal.aborted) break;
          out = approved
            ? await dispatchWrite(t.name, t.input, client, plugin)
            : { ok: false, content: "The user declined this change — do not retry it; continue without it." };
        } else {
          out = await dispatchWpRead(t.name, t.input, client);
        }
        patchChat({ chips: chatLive.chips.map(x => x.id === t.id ? { ...x, ok: out.ok } : x) });
        clientResults.push({ type: "tool_result", tool_use_id: t.id, content: out.content, is_error: !out.ok });
      }
      if (ac.signal.aborted) break;
      wire = [...(sink.v.messages as WireMessage[]), { role: "user", content: [...sink.v.serverResults, ...clientResults] }];
    }
  } finally {
    if (acc.trim()) appendChatHistory({ role: "assistant", content: acc });
    chatAbort = null;
    patchChat({ streaming: false, liveText: "", chips: [], pending: null });
  }
}

export function ConversationalSeo({ siteUrl, siteBrand, connected, getClient, plugin }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try { const v = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]"); return Array.isArray(v) ? v : []; } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [siteId, setSiteId] = useState<string | null>(null);
  const [siteResolved, setSiteResolved] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false); // expand/collapse the proposed-change preview
  const scrollRef = useRef<HTMLDivElement>(null);

  // Live streaming state lives at MODULE level (chatLive) so an in-flight reply survives this
  // component unmounting on a tab switch. Subscribe: re-render on any change, and reload the
  // transcript (the turn loop appends the finished reply straight to localStorage).
  const [, bump] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    // Re-render on every change (cheap — live text/chips come from chatLive). Only reload the
    // transcript when NOT streaming, i.e. when a finished reply was just appended — avoids
    // re-parsing the whole history on every streamed token.
    const onChange = () => { bump(); if (!chatLive.streaming) { try { const v = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]"); setMessages(Array.isArray(v) ? v : []); } catch { /* ignore */ } } };
    chatSubs.add(onChange);
    return () => { chatSubs.delete(onChange); };
  }, []);
  const { streaming, liveText, chips, error, pending: pendingApproval } = chatLive;
  const resolveApproval = chatResolveApproval;

  // Persist a trimmed transcript (last 40 turns) so per-site history stays within the storage budget.
  useEffect(() => { try { localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-40))); } catch { /* ignore */ } }, [messages]);
  // Collapse the proposed-change preview whenever a new approval appears.
  useEffect(() => { setPreviewOpen(false); }, [pendingApproval?.id]);
  // A reply is saved once its turn finishes. If the page is refreshed/closed WHILE one is still
  // streaming, commit the partial from the module session so it isn't lost. (A tab switch no longer
  // needs this — the turn keeps running at module level.)
  useEffect(() => {
    const onUnload = () => { if (chatLive.streaming && chatLive.liveText.trim()) appendChatHistory({ role: "assistant", content: chatLive.liveText }); };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, liveText, chips, pendingApproval]);

  // Resolve the saved-site id for the connected site (data tools need it server-side).
  useEffect(() => {
    let alive = true;
    setSiteResolved(false); setSiteId(null);
    if (!connected || !siteUrl) { setSiteResolved(true); return; }
    listSites()
      .then(sites => { if (!alive) return; const host = siteKey(siteUrl); const m = sites.find(s => siteKey(s.siteUrl) === host); setSiteId(m?.id ?? null); })
      .catch(() => { if (alive) setSiteId(null); })
      .finally(() => { if (alive) setSiteResolved(true); });
    return () => { alive = false; };
  }, [siteUrl, connected]);

  const canChat = connected && !!siteId;

  async function send() {
    const text = input.trim();
    if (!text || chatLive.streaming || !siteId) return;
    const visibleNext: ChatMessage[] = [...messages, { role: "user", content: text }];
    setMessages(visibleNext);
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(visibleNext.slice(-40))); } catch { /* ignore */ }
    setInput("");
    // Fire the module-level turn loop — it survives this component unmounting on a tab switch.
    void runChatTurn(siteId, visibleNext, getClient, plugin ?? "unknown");
  }

  function stop() { chatResolveApproval(false); chatAbort?.abort(); }
  function clearChat() { if (chatLive.streaming) return; setMessages([]); try { localStorage.setItem(HISTORY_KEY, "[]"); } catch { /* ignore */ } patchChat({ error: "" }); }
  function exportChat() {
    const md = messages.map(m => `**${m.role === "user" ? "You" : "Assistant"}:**\n\n${m.content}`).join("\n\n---\n\n");
    try {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([md], { type: "text/markdown" }));
      a.download = `conversational-seo-${siteKey(siteUrl || "chat") || "chat"}.md`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 0);
    } catch { /* ignore */ }
  }

  // Fill the flex parent (App pins the connect band above and gives us the rest of the viewport)
  // rather than dictating a fixed height, so the page never gains a second scrollbar. The only
  // scroll region is the messages list below.
  // Theme-aware palette (reuses the dark values used across the other tabs) so the inline-styled
  // chat bubbles / approval panel / composer match the app in dark mode. Reads the theme on render;
  // App re-renders the tree on theme toggle so this stays in sync.
  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const ink       = isDark ? "#e6edf6" : "#0f172a";
  const ink2      = isDark ? "#93a4c0" : "#64748b";
  const inkMuted  = isDark ? "#7c8aa0" : "#94a3b8";
  const inkMid    = isDark ? "#cbd5e1" : "#334155";
  const cardBorder = isDark ? "rgba(255,255,255,.14)" : "#e2e8f0";
  const chipBg    = isDark ? "#243149" : "#fff";
  const asstBg    = isDark ? "#182437" : "#fef8f2";
  const asstBorder = isDark ? "rgba(255,255,255,.12)" : "#f0ddc6";
  const red       = isDark ? "#f87171" : "#dc2626";

  const box: React.CSSProperties = { fontFamily: "'Inter','Mulish',sans-serif", color: ink, background: isDark ? "transparent" : "#ffffff", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 };

  return (
    <div style={box}>
      <div className="sh" style={{ flexShrink: 0 }}>
        <h2>Conversational SEO</h2>
        <p style={{ maxWidth: "none", fontSize: 11 }}>Chat with Claude about your Search Console &amp; Analytics data and your biggest opportunity gaps vs. competitors.</p>
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 2px 12px", display: "flex", flexDirection: "column", gap: 14 }}>
        {messages.length === 0 && !streaming && (
          <div style={{ margin: "auto", textAlign: "center", color: inkMuted, maxWidth: 560 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: inkMid, marginBottom: 12 }}>Ask about your site's SEO</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
              {PROMPT_SUGGESTIONS.map(s => (
                <button key={s} onClick={() => setInput(s)} disabled={!canChat} style={{ fontSize: 12, color: inkMid, background: chipBg, border: `1px solid ${cardBorder}`, borderRadius: 20, padding: "7px 13px", cursor: canChat ? "pointer" : "default", lineHeight: 1.3, textAlign: "left" }}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", ...(m.role === "user" ? { paddingRight: 14, margin: "6px 0" } : null) }}>
            <div style={{
              maxWidth: "85%", padding: "10px 14px", borderRadius: 12, fontSize: 13.5, lineHeight: 1.55,
              background: m.role === "user" ? "#16a34a" : asstBg,
              color: m.role === "user" ? "#fff" : ink,
              border: m.role === "user" ? "none" : `1px solid ${asstBorder}`,
            }}>
              {m.role === "assistant"
                ? <div className="cseo-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(fixSpacing(m.content)) }} />
                : <span style={{ whiteSpace: "pre-wrap" }}>{m.content}</span>}
            </div>
          </div>
        ))}
        {/* Live streaming bubble */}
        {streaming && (
          <div style={{ display: "flex", justifyContent: "flex-start", flexDirection: "column", gap: 6 }}>
            {chips.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {chips.map(c => (
                  <span key={c.id} title={c.error} style={{
                    fontSize: 10.5, fontWeight: 700, padding: "3px 9px", borderRadius: 20,
                    background: c.ok === false ? (isDark ? "rgba(239,68,68,.14)" : "#fef2f2") : c.ok ? (isDark ? "rgba(22,163,74,.16)" : "#f0fdf4") : (isDark ? "rgba(37,99,235,.16)" : "#eff6ff"),
                    color: c.ok === false ? (isDark ? "#fca5a5" : "#dc2626") : c.ok ? (isDark ? "#86efac" : "#16a34a") : (isDark ? "#93c5fd" : "#2563eb"),
                    border: `1px solid ${c.ok === false ? (isDark ? "rgba(239,68,68,.4)" : "#fecaca") : c.ok ? (isDark ? "rgba(22,163,74,.4)" : "#bbf7d0") : (isDark ? "rgba(37,99,235,.4)" : "#bfdbfe")}`,
                  }}>{c.ok === false ? "✗" : c.ok ? "✓" : "⋯"} {toolLabel(c.name)}</span>
                ))}
              </div>
            )}
            <div style={{ maxWidth: "85%", padding: "10px 14px", borderRadius: 12, fontSize: 13.5, lineHeight: 1.55, background: asstBg, color: ink, border: `1px solid ${asstBorder}` }}>
              {liveText
                ? <div className="cseo-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(fixSpacing(liveText)) }} />
                : <span style={{ color: inkMuted }}>Thinking…</span>}
            </div>
          </div>
        )}
        {/* WordPress write approval — rich preview/diff */}
        {pendingApproval && (
          <div style={{ alignSelf: "stretch", background: isDark ? "#152238" : "#eef4fb", border: `1px solid ${isDark ? "rgba(255,255,255,.12)" : "#c7d9ef"}`, borderRadius: 12, padding: "12px 14px" }}>
            {/* Title — matches the Conversational SEO tab heading (Oswald, uppercase, orange). */}
            <div style={{ fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontSize: 15, fontWeight: 500, letterSpacing: .7, textTransform: "uppercase", color: "#f97316", marginBottom: 8 }}>Approve website change?</div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: ink, lineHeight: 1.5, marginBottom: 10 }}>{pendingApproval.detail.summary}</div>
            {pendingApproval.detail.warning && (
              <div style={{ fontSize: 12, color: isDark ? "#fca5a5" : "#b91c1c", background: isDark ? "rgba(239,68,68,.12)" : "#fef2f2", border: `1px solid ${isDark ? "rgba(239,68,68,.4)" : "#fecaca"}`, borderRadius: 6, padding: "6px 9px", marginBottom: 10 }}>⚠ {pendingApproval.detail.warning}</div>
            )}
            {/* Fields — each label + value stacked and flush-left for a clean read. */}
            {pendingApproval.detail.rows.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                {pendingApproval.detail.rows.map((r, i) => (
                  <div key={i} style={{ marginBottom: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase", color: ink2, marginBottom: 2 }}>{r.label}</div>
                    <div style={{ fontSize: 12.5, color: inkMid, wordBreak: "break-word", lineHeight: 1.45 }}>{r.value}</div>
                  </div>
                ))}
              </div>
            )}
            {(pendingApproval.detail.before !== undefined || pendingApproval.detail.after !== undefined) && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 11.5, background: isDark ? "rgba(239,68,68,.10)" : "#fef2f2", border: `1px solid ${isDark ? "rgba(239,68,68,.35)" : "#fecaca"}`, borderRadius: 6, padding: "6px 9px" }}><div style={{ fontWeight: 700, color: isDark ? "#fca5a5" : "#b91c1c", marginBottom: 3 }}>Before</div><div style={{ color: isDark ? "#aeb9cc" : "#475569", lineHeight: 1.45 }}>{pendingApproval.detail.before || "(empty)"}</div></div>
                <div style={{ fontSize: 11.5, background: isDark ? "rgba(22,163,74,.12)" : "#f0fdf4", border: `1px solid ${isDark ? "rgba(22,163,74,.4)" : "#bbf7d0"}`, borderRadius: 6, padding: "6px 9px" }}><div style={{ fontWeight: 700, color: isDark ? "#86efac" : "#16a34a", marginBottom: 3 }}>After</div><div style={{ color: isDark ? "#aeb9cc" : "#475569", lineHeight: 1.45 }}>{pendingApproval.detail.after || "(empty)"}</div></div>
              </div>
            )}
            {/* Proposed change — collapsed by default; the arrow expands it to show the full content
                (e.g. all schema) instead of a truncated preview. */}
            {pendingApproval.detail.preview && (
              <div style={{ marginBottom: 10 }}>
                <button
                  onClick={() => setPreviewOpen(o => !o)}
                  style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", background: "none", border: "none", padding: "0 0 4px", cursor: "pointer", fontSize: 10, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase", color: ink2, textAlign: "left" }}
                >
                  <span style={{ display: "inline-block", transition: "transform .15s", transform: previewOpen ? "rotate(90deg)" : "none" }}>▶</span>
                  Proposed change{previewOpen ? "" : " (show all)"}
                </button>
                <div style={{ fontSize: 11.5, color: inkMid, background: isDark ? "rgba(0,0,0,.22)" : "#fff", border: `1px solid ${isDark ? "rgba(255,255,255,.14)" : "#dbe4ef"}`, borderRadius: 6, padding: "8px 10px", maxHeight: previewOpen ? 400 : 120, overflowY: previewOpen ? "auto" : "hidden", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{pendingApproval.detail.preview}</div>
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => resolveApproval(true)} style={{ padding: "7px 18px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 0, fontSize: 12.5, fontWeight: 700, letterSpacing: ".3px", fontFamily: "'Inter','Mulish',sans-serif", cursor: "pointer" }}>Approve</button>
              <button onClick={() => resolveApproval(false)} style={{ padding: "7px 18px", background: isDark ? "transparent" : "#fff", color: isDark ? "#cbd5e1" : "#334155", border: `1px solid ${isDark ? "rgba(255,255,255,.22)" : "#cbd5e1"}`, borderRadius: 0, fontSize: 12.5, fontWeight: 700, letterSpacing: ".3px", fontFamily: "'Inter','Mulish',sans-serif", cursor: "pointer" }}>Reject</button>
            </div>
          </div>
        )}
      </div>

      {error && <div style={{ flexShrink: 0, fontSize: 12, color: red, padding: "6px 2px" }}>⚠ {error}</div>}

      {/* Composer / gating */}
      {!siteResolved ? (
        <div style={{ flexShrink: 0, fontSize: 12.5, color: inkMuted, padding: "10px 2px" }}>Loading…</div>
      ) : !connected ? (
        <div style={{ flexShrink: 0, fontSize: 12.5, color: ink2, padding: "10px 12px", background: isDark ? "rgba(255,255,255,.04)" : "#f8fafc", border: `1px solid ${cardBorder}`, borderRadius: 8 }}>Connect your WordPress site (top of the page) to start.</div>
      ) : !siteId ? (
        <div style={{ flexShrink: 0, fontSize: 12.5, color: isDark ? "#fcd34d" : "#64748b", padding: "10px 12px", background: isDark ? "rgba(245,158,11,.10)" : "#fffbeb", border: `1px solid ${isDark ? "rgba(245,158,11,.35)" : "#fde68a"}`, borderRadius: 8 }}>
          Save this website in <strong>Settings → Saved Websites</strong> (and connect Search Console / Analytics + set your API keys) to enable the assistant's data tools.
        </div>
      ) : (
        <div style={{ flexShrink: 0, display: "flex", gap: 8, alignItems: "flex-end", paddingTop: 8, borderTop: `1px solid ${cardBorder}` }}>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }}
            placeholder={`Ask about ${siteBrand || "your site"}'s SEO…  (Enter to send, Shift+Enter for a new line)`}
            rows={4}
            disabled={streaming}
            style={{ flex: 1, boxSizing: "border-box", resize: "vertical", padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 13, fontFamily: "'Inter','Mulish',sans-serif", lineHeight: 1.5, color: "#0f172a", outline: "none", minHeight: 88 }}
          />
          {streaming ? (
            <button onClick={stop} style={{ padding: "10px 16px", background: isDark ? "#243149" : "#e2e8f0", color: isDark ? "#dbe3ee" : "#334155", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>Stop</button>
          ) : (
            <button onClick={() => void send()} disabled={!input.trim() || !canChat} style={{ padding: "10px 18px", background: (!input.trim() || !canChat) ? "#93c5a3" : "#16a34a", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: (!input.trim() || !canChat) ? "default" : "pointer", whiteSpace: "nowrap" }}>Send</button>
          )}
          {messages.length > 0 && !streaming && (
            <>
              <button onClick={exportChat} title="Download this conversation as Markdown" style={{ padding: "10px 14px", background: "linear-gradient(135deg,#ea580c,#f97316)", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>Export</button>
              <button onClick={clearChat} title="Start a new conversation" style={{ padding: "10px 14px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>New chat</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
